import React, { useState, useEffect, useCallback } from 'react';
import { Spinner, Alert, Row, Col, Card, Form, Button, Badge } from 'react-bootstrap';
import { toast } from 'react-toastify';
import api from '../config/api';
import './PhotoFilters.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faImage, faMagic, faSync } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../context/AuthContext';

const PhotoFilters = ({ onProcessingComplete }) => {
  const [filters, setFilters] = useState([]);
  const [loadingFilters, setLoadingFilters] = useState(false);
  const [error, setError] = useState('');
  
  const [sourceImageFile, setSourceImageFile] = useState(null);
  const [sourceImageUrl, setSourceImageUrl] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStatus, setProcessingStatus] = useState('');
  const [activeFilterId, setActiveFilterId] = useState(null);

  // Yeni state'ler - filtre sonuçları için
  const [filterResults, setFilterResults] = useState({});
  const [processingFilters, setProcessingFilters] = useState(new Set());
  const [completedFilters, setCompletedFilters] = useState(new Set());
  const [isApplyingAllFilters, setIsApplyingAllFilters] = useState(false);

  // Günlük kullanım sınırı (localStorage ile)
  const DAILY_FILTER_LIMIT = 2;
  const { user } = useAuth();
  const [remainingFilterUsage, setRemainingFilterUsage] = useState(DAILY_FILTER_LIMIT);

  const getTodayDateString = () => {
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  };

  const getUserIdForStorage = () => {
    if (user && user.id) return user.id;
    let anonymousId = localStorage.getItem('anonymousUserId');
    if (!anonymousId) {
      anonymousId = 'anonymous_' + Math.random().toString(36).substring(2, 15);
      localStorage.setItem('anonymousUserId', anonymousId);
    }
    return anonymousId;
  };

  const readFilterUsage = () => {
    const userId = getUserIdForStorage();
    const today = getTodayDateString();
    const storageKey = `ai_filter_usage_${userId}`;
    const raw = localStorage.getItem(storageKey);
    if (!raw) return { date: today, count: 0 };
    try {
      const parsed = JSON.parse(raw);
      if (parsed.date !== today) return { date: today, count: 0 };
      return parsed;
    } catch {
      return { date: today, count: 0 };
    }
  };

  const writeFilterUsage = (usage) => {
    const userId = getUserIdForStorage();
    const storageKey = `ai_filter_usage_${userId}`;
    localStorage.setItem(storageKey, JSON.stringify(usage));
  };

  const incrementFilterUsage = () => {
    const today = getTodayDateString();
    const usage = readFilterUsage();
    const updated = { date: today, count: (usage.count || 0) + 1 };
    writeFilterUsage(updated);
    const remaining = Math.max(0, DAILY_FILTER_LIMIT - updated.count);
    setRemainingFilterUsage(remaining);
    return remaining;
  };

  // İlk yüklemede kalan hakkı hesapla
  useEffect(() => {
    const today = getTodayDateString();
    const usage = readFilterUsage();
    // Kayıt yoksa veya tarih değişmişse LS'e başlangıç kaydı yaz
    if (!usage || usage.date !== today) {
      const initial = { date: today, count: 0 };
      writeFilterUsage(initial);
      setRemainingFilterUsage(DAILY_FILTER_LIMIT);
    } else {
      setRemainingFilterUsage(Math.max(0, DAILY_FILTER_LIMIT - (usage.count || 0)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // CSS bar animasyonunun sürekli resetlenmesine neden olan gereksiz progress state'i kaldırıldı

  // Filtreleri yükle
  useEffect(() => {
    const fetchFilters = async () => {
      try {
        setLoadingFilters(true);
        setError(null);
        const response = await api.get('/ai-filter/filters');
        if (response.data && Array.isArray(response.data)) {
          // Backend isimleri: id, name, description
          // UI beklentisine uyarlayalım
          setFilters(response.data.map(f => ({
            id: f.id,
            asset_id: f.id,
            asset_name: f.name,
            description: f.description,
            asset_image: f.image
          })));
        } else {
          throw new Error('Geçersiz veri formatı');
        }
      } catch (error) {
        console.error('Error fetching filters:', error);
        setError('Filtreler yüklenirken bir hata oluştu. Lütfen sayfayı yenileyin.');
        // Basit fallback
        setFilters([
          { id: 'american_comic', asset_id: 'american_comic', asset_name: 'Amerikan Çizgi Roman Stili', description: 'Kalın çizgiler, parlak renkler ve klasik çizgi roman havası' },
          { id: 'pixar_cartoon', asset_id: 'pixar_cartoon', asset_name: 'Çizgi Film (Pixar Benzeri)', description: 'Yumuşak 3D benzeri çizgi film estetiği' }
        ]);
      } finally {
        setLoadingFilters(false);
      }
    };
    fetchFilters();
  }, []);

  const handleUpload = useCallback(async (file) => {
    if (!file) return;

    setIsUploading(true);
    setError('');
    // Önceki filtre sonuçlarını temizle
    setFilterResults({});
    setProcessingFilters(new Set());
    setCompletedFilters(new Set());
    
    try {
      const formData = new FormData();
      formData.append('image', file);
      const response = await api.post('/ai-filter/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (response.data.success) {
        setSourceImageUrl(response.data.imageUrl);
        toast.success('Görseliniz yüklendi. Bir filtre seçin.');
      } else {
        throw new Error('Görsel yüklenemedi.');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Görsel yüklenirken bir hata oluştu.';
      setError(errorMsg);
      console.error(err);
      setSourceImageUrl(''); // Hata durumunda önizlemeyi temizle
    } finally {
      setIsUploading(false);
    }
  }, [filters]); // filters'ı dependency olarak ekle

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 15 * 1024 * 1024) {
        toast.error('Dosya boyutu 15 MB\'dan büyük olamaz.');
        return;
      }
      setSourceImageFile(file);
      // Geçici olarak blob URL ile önizleme göster
      setSourceImageUrl(URL.createObjectURL(file));
      // Yüklemeyi otomatik başlat
      handleUpload(file);
    }
  };

  // Tüm filtreleri otomatik olarak uygula
  const applyAllFilters = async () => {
    if (!filters || filters.length === 0 || !sourceImageFile) return;

    // Günlük limit kontrolü
    if (remainingFilterUsage <= 0) {
      toast.error(`Günlük ${DAILY_FILTER_LIMIT} filtre uygulama hakkınızı doldurdunuz. Lütfen yarın tekrar deneyin.`);
      return;
    }

    // Zaten tamamlanan veya işlenenleri hariç tut, kalan haktan fazlasını başlatma
    const candidateFilters = filters.filter(f => {
      const id = f.asset_id || f.id;
      return !processingFilters.has(id) && !completedFilters.has(id);
    });

    const toProcess = candidateFilters.slice(0, remainingFilterUsage);

    if (toProcess.length < candidateFilters.length) {
      toast.info(`Günlük limit nedeniyle yalnızca ${toProcess.length} filtre uygulanacak.`);
    }

    if (toProcess.length === 0) {
      toast.info('Uygulanacak yeni filtre bulunamadı.');
      return;
    }

    setIsApplyingAllFilters(true);
    setProcessingFilters(prev => {
      const s = new Set(prev);
      toProcess.forEach(f => s.add(f.asset_id || f.id));
      return s;
    });

    const formDatas = toProcess.map(filter => {
      const fd = new FormData();
      fd.append('filter', filter.id);
      fd.append('size', '1024x1536');
      if (sourceImageUrl) {
        fd.append('imageUrl', sourceImageUrl);
      }
      return { id: filter.id, fd };
    });

    // Poll helper
    const pollJob = async (jobId) => {
      const maxAttempts = 60; // ~2 dk
      const delay = (ms) => new Promise(res => setTimeout(res, ms));
      for (let i = 0; i < maxAttempts; i++) {
        try {
          const r = await api.get(`/ai-filter/jobs/${jobId}`);
          const job = r.data?.job;
          if (job?.status === 'completed' && job?.result_image_url) {
            return job.result_image_url;
          }
          if (job?.status === 'failed') {
            throw new Error(job?.error_message || 'İş başarısız');
          }
        } catch {}
        await delay(2000);
      }
      throw new Error('Zaman aşımı');
    };

    const promises = formDatas.map(async ({ id, fd }) => {
      try {
        const resp = await api.post('/ai-filter/apply', fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        const jobId = resp.data?.job_id;
        if (!jobId) throw new Error('İş kimliği alınamadı');
        const resultUrl = await pollJob(jobId);
        setFilterResults(prev => ({ ...prev, [id]: resultUrl }));
        setProcessingFilters(prev => { const s = new Set(prev); s.delete(id); return s; });
        setCompletedFilters(prev => { const s = new Set(prev); s.add(id); return s; });
        incrementFilterUsage();
        return resultUrl;
      } catch (e) {
        console.error('Filtre hatası', id, e);
        setProcessingFilters(prev => { const s = new Set(prev); s.delete(id); return s; });
        return null;
      }
    });

    try {
      const results = await Promise.allSettled(promises);
      const ok = results.filter(r => r.status === 'fulfilled' && r.value).length;
      toast.success(`${ok} filtre uygulandı`);
    } finally {
      setIsApplyingAllFilters(false);
    }
  };

  const applyAIFilter = async (filter) => {
    // Günlük limit kontrolü
    if (remainingFilterUsage <= 0) {
      toast.error(`Günlük ${DAILY_FILTER_LIMIT} filtre uygulama hakkınızı doldurdunuz. Lütfen yarın tekrar deneyin.`);
      return;
    }

    const filterId = filter.asset_id || filter.id;
    const existing = filterResults[filterId];
    if (existing) {
      toast.success('Filtreli görsel hazır!');
      onProcessingComplete(existing);
      return;
    }
    const fd = new FormData();
    fd.append('filter', filterId);
    fd.append('size', '1024x1536');
    if (sourceImageUrl) {
      fd.append('imageUrl', sourceImageUrl);
    }
    setProcessingStatus(`${filter.asset_name} uygulanıyor...`);
    setIsProcessing(true);
    setProcessingFilters(prev => { const s = new Set(prev); s.add(filterId); return s; });
    try {
      const resp = await api.post('/ai-filter/apply', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const jobId = resp.data?.job_id;
      if (!jobId) throw new Error('İş kimliği alınamadı');
      // Poll
      const delay = (ms) => new Promise(res => setTimeout(res, ms));
      let resultUrl = null;
      for (let i = 0; i < 60; i++) {
        const r = await api.get(`/ai-filter/jobs/${jobId}`);
        const job = r.data?.job;
        if (job?.status === 'completed' && job?.result_image_url) {
          resultUrl = job.result_image_url;
          break;
        }
        if (job?.status === 'failed') {
          throw new Error(job?.error_message || 'İş başarısız');
        }
        await delay(2000);
      }
      if (!resultUrl) throw new Error('Zaman aşımı');
      setFilterResults(prev => ({ ...prev, [filterId]: resultUrl }));
      setCompletedFilters(prev => { const s = new Set(prev); s.add(filterId); return s; });
      onProcessingComplete(resultUrl);
      toast.success('Filtre uygulandı');
      incrementFilterUsage();
    } catch (e) {
      console.error(e);
      toast.error('Filtre uygulanamadı');
    } finally {
      setIsProcessing(false);
      setProcessingStatus('');
      setProcessingFilters(prev => { const s = new Set(prev); s.delete(filterId); return s; });
    }
  };

  const FilterItem = ({ filter, onSelect }) => {
    const [imageError, setImageError] = useState(false);
    const [imageLoaded, setImageLoaded] = useState(false);
    
    const filterId = filter.asset_id || filter.id;
    const isProcessing = processingFilters.has(filterId);
    const isCompleted = completedFilters.has(filterId);
    const filteredImageUrl = filterResults[filterId];
    
    // Gösterilecek görseli belirle
    const displayImage = filteredImageUrl || filter.preview_url || filter.asset_image;

    const handleImageError = () => {
      setImageError(true);
      setImageLoaded(true);
    };

    const handleImageLoad = () => {
      setImageLoaded(true);
    };

    // Eğer işleniyor durumundaysa, processing ekranını göster
    if (isProcessing) {
      return (
        <div className="filter-item processing">
          <div className="filter-processing-container">
            <div className="filter-processing-backdrop">
              {sourceImageUrl && (
                <img src={sourceImageUrl} alt="İşleme önizlemesi" />
              )}
            </div>
            <div className="filter-processing-content">
              <div className="filter-magic-wand">
                <FontAwesomeIcon icon={faMagic} className="filter-wand-icon" />
                <div className="filter-sparkle filter-sparkle1"></div>
                <div className="filter-sparkle filter-sparkle2"></div>
                <div className="filter-sparkle filter-sparkle3"></div>
              </div>
              
              <div className="filter-processing-text">
                <h6>İşleniyor</h6>
                <div className="filter-progress-container">
                  <div className="filter-progress-bar">
                    <div className="filter-progress-fill">
                      <div className="filter-shine-effect"></div>
                      <div className="filter-glow-point"></div>
                    </div>
                  </div>
                  <div className="filter-floating-particles">
                    {[...Array(3)].map((_, i) => (
                      <div key={i} className={`filter-particle filter-particle-${i + 1}`} />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div 
        className={`filter-item ${!imageLoaded ? 'placeholder' : ''} ${isCompleted ? 'completed' : ''}`} 
        onClick={() => onSelect(filter)}
        style={{ cursor: 'pointer' }}
      >        
        {!imageError ? (
          <img
            src={displayImage}
            alt={filter.asset_name}
            onError={handleImageError}
            onLoad={handleImageLoad}
            style={{ 
              display: imageLoaded ? 'block' : 'none',
              transition: 'opacity 0.3s ease'
            }}
          />
        ) : (
          <div className="filter-error">
            <span>Görsel Yüklenemedi</span>
          </div>
        )}
        
        <div className="filter-info">
          <h5 className="filter-name">{filter.asset_name}</h5>
          {!imageError && (
            <p className="filter-description">
              {isCompleted ? 'Hazır!' : filter.description || 'Özel filtre efekti'}
            </p>
          )}
        </div>
      </div>
    );
  };

  const FilterGrid = ({ filters, onFilterSelect }) => (
    <div className="filter-grid">
      {filters.map((filter) => (
        <FilterItem
          key={filter.asset_id || filter.id}
          filter={filter}
          onSelect={onFilterSelect}
        />
      ))}
    </div>
  );

  // Büyük ekran artık yok

  return (
    <div className="photo-filters-container">
      {/* Step 1: Upload */}
      <div className="step-container">
        <div className="step-header">
          <span className="step-number">1</span>
          <h5>Fotoğrafınızı Seçin</h5>
          <div className="ms-auto d-none d-md-block">
            <Badge bg="info">Kalan hak: {remainingFilterUsage} / {DAILY_FILTER_LIMIT}</Badge>
          </div>
        </div>
        {!sourceImageUrl ? (
          <Form.Group controlId="fileUpload" className="upload-zone">
            <Form.Label className="upload-zone-label">
              <FontAwesomeIcon icon={faImage} size="3x" className="upload-icon" />
              <p className="mt-2">Görseli buraya sürükleyin veya seçmek için tıklayın</p>
              <span className="text-muted small">PNG, JPG, WEBP (Maks. 15MB)</span>
            </Form.Label>
            <Form.Control type="file" accept="image/jpeg,image/png,image/webp" onChange={handleFileChange} className="d-none" />
          </Form.Group>
        ) : (
          <div className="file-info-card">
            <FontAwesomeIcon icon={faImage} size="2x" className="file-icon" />
            <div className="file-details">
              <span className="file-name">{sourceImageFile?.name}</span>
              <span className="file-size">{(sourceImageFile?.size / (1024 * 1024)).toFixed(2)} MB</span>
            </div>
            {isUploading && (
              <Spinner animation="border" size="sm" className="ms-2 text-primary" />
            )}
          </div>
        )}
      </div>
      
      {/* Step 2: Select Filter */}
      <div className={`step-container mt-4 ${(!sourceImageUrl || isUploading || sourceImageUrl.startsWith('blob:')) ? 'disabled' : ''}`}>
        <div className="step-header">
          <span className="step-number">2</span>
          <h5>Bir Filtre Seçin</h5>
          <div className="ms-auto d-md-none">
            <Badge bg="info">Kalan: {remainingFilterUsage}/{DAILY_FILTER_LIMIT}</Badge>
          </div>
          {isApplyingAllFilters && processingFilters.size > 0 && (
            <div className="ms-2">
              <span className="text-muted small">
                {processingFilters.size} filtre işleniyor...
              </span>
            </div>
          )}
        </div>
        {loadingFilters ? (
            <div className="text-center p-5"><Spinner animation="border" /></div>
        ) : (
          <FilterGrid filters={filters} onFilterSelect={applyAIFilter} />
        )}
      </div>

      {error && <Alert variant="danger" className="mt-3">{error}</Alert>}
    </div>
  );
};

export default PhotoFilters; 